// What the app edits and keeps on the device. A project is one site visit: the customer, the
// rooms as measured, the design choices, the render the customer approved and the sign-off.
// Sending turns it into an immutable survey.v1 revision (src/survey/build.ts).

import type {
  Choices,
  Measurement,
  Note,
  ObstructionKind,
  OpeningKind,
  PartitionPurpose,
  Swing,
  UtilityKind,
  ZoneKind,
} from "../survey/types";

export type M = Measurement | null;

export interface OpeningDraft {
  id: string;
  kind: OpeningKind;
  wall: number;
  offset: M;
  width: M;
  sill: M;
  height: M;
  reveal_depth?: M;
  swing?: Swing;
}

export interface UtilityDraft {
  id: string;
  kind: UtilityKind;
  wall: number;
  offset: M;
  height: M;
}

export interface ObstructionDraft {
  id: string;
  kind: ObstructionKind;
  wall: number;
  offset: M;
  width: M;
  elevation: M;
  height: M;
  depth: M;
}

export interface PartitionDraft {
  id: string;
  offset: number; // µm from the zone start
  width: number; // µm
  purpose: PartitionPurpose;
  label?: string;
}

export interface ZoneDraft {
  id: string;
  name: string;
  kind: ZoneKind;
  wall: number;
  offset: number;
  length: number;
  elevation: number;
  height: number;
  depth: number;
  partitions: PartitionDraft[];
}

export interface DiagonalDraft {
  a: number;
  b: number;
  length: M;
}

export interface RoomDraft {
  id: string;
  name: string;
  /** rough shape, clockwise, metres (template or taps) */
  sketch: [number, number][];
  walls: M[];
  /** length readings at floor, middle and ceiling level; the wall length is the shortest */
  readings: M[][];
  /** heights measured along each wall (floor to ceiling) */
  heights: Measurement[][];
  diagonals: DiagonalDraft[];
  ceiling: M;
  openings: OpeningDraft[];
  utilities: UtilityDraft[];
  obstructions: ObstructionDraft[];
  zones: ZoneDraft[];
  photo_ids: string[];
}

export interface PhotoRef {
  id: string;
  sha256: string;
  content_type: "image/jpeg" | "image/png" | "image/heic";
  size_bytes: number;
  width_px?: number;
  height_px?: number;
  taken_at: string;
  caption?: string;
  /** what the photo is of: a room, wall or other id */
  subject?: string;
}

export type ChoiceSlot = "carcass" | "front" | "handle" | "worktop" | "style";

export interface PickedProduct {
  product_id: string;
  name: string;
  sku: string;
  slot: ChoiceSlot | null;
  unit: string;
  qty: number;
  min_price_mnt: number | null;
  category: string;
  /** render asset to place in a render, when the product has one */
  asset_id?: string | null;
  thumb_url?: string | null;
}

export interface RenderItemDraft {
  asset_id: string;
  product_id?: string;
  title: string;
  /** surfaces (fronts, boards, worktops) are sent with a quad */
  mode: "object" | "surface";
  /** normalised to the photo (0..1, origin top left) */
  center_x: number;
  center_y: number;
  width: number;
  /** surfaces: share of the photo height (the quad is this rectangle) */
  height?: number;
  /** objects: the product's real height / width, when known (the server uses the same) */
  aspect?: number | null;
  rotation_deg: number;
  /** what the rep tells the model about this item ("цагаан өнгөтэй", "ханан дээр") */
  note?: string;
  thumb_url?: string | null;
  /** the best nearby offer when the item was placed */
  price_mnt?: number | null;
  supplier?: string | null;
}

export interface RenderResultRow {
  id: string;
  status: string;
  output_url: string | null;
  created_at: string;
  error_code: string | null;
  quality?: string;
  progress?: number;
  items?: number;
  seconds?: number | null;
  /** the rep's wish for the whole render, as sent */
  note?: string | null;
  /** the AI model and resolution that made it */
  engine?: string | null;
  resolution?: string | null;
}

export interface RenderState {
  base_photo_id: string | null;
  scene_id: string | null;
  /** the photo the scene was created from (a new photo needs a new scene) */
  scene_photo_id: string | null;
  items: RenderItemDraft[];
  results: RenderResultRow[];
  approved: { result_id: string; approved_at: string; output_url: string | null } | null;
}

export type RevisionState = "queued" | "sending" | "sent" | "rejected";

export interface RevisionRecord {
  revision_id: string;
  parent_revision_id: string | null;
  created_at: string;
  sha256: string;
  size_bytes: number;
  state: RevisionState;
  revision_no?: number;
  warnings?: { code: string; message_mn: string }[];
  errors?: { code: string; path: string; message_mn: string }[];
  photos_pending?: number;
  /** content hash without ids and time: equal means nothing changed since this revision */
  content_key: string;
}

export interface Project {
  id: string; // survey_id
  /** the organization and user it was captured for: a shared tablet shows each only theirs */
  org_id: string;
  user_id: string;
  title: string;
  deal_id: string | null;
  deal_title: string | null;
  created_at: string;
  updated_at: string;
  /** last change made by the rep (not by sending) */
  edited_at: string;
  site: { address: string; customer_name: string; customer_phone: string; lat?: number; lng?: number };
  surveyor_name: string;
  choices: Choices;
  picks: PickedProduct[];
  rooms: RoomDraft[];
  photos: PhotoRef[];
  notes: Note[];
  render: RenderState;
  sign_off: { name: string; signed_at: string; statement: string; photo_id: string } | null;
  revisions: RevisionRecord[];
  /** id counter for openings, zones, photos, notes */
  seq: number;
}

export function nextId(project: Project, prefix: string): string {
  project.seq += 1;
  return `${prefix}${project.seq}`;
}

export const WALL_LETTERS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";

export function wallLabel(i: number): string {
  return WALL_LETTERS.charAt(i) || `W${i + 1}`;
}

export function cornerLabel(i: number): string {
  return String(i + 1);
}

/** survey.v1 ids are unique in the whole document, so room parts carry the room id. */
export const ids = {
  corner: (room: RoomDraft, i: number) => `${room.id}.C${i + 1}`,
  wall: (room: RoomDraft, i: number) => `${room.id}.W${wallLabel(i)}`,
  diagonal: (room: RoomDraft, a: number, b: number) => `${room.id}.D${a + 1}-${b + 1}`,
};

export const SHAPES: { key: string; name: string; sketch: [number, number][] }[] = [
  { key: "rect", name: "Тэгш өнцөгт", sketch: [[0, 0], [0, 3], [4, 3], [4, 0]] },
  { key: "l", name: "Г хэлбэр", sketch: [[0, 0], [0, 4], [2.2, 4], [2.2, 2], [4.5, 2], [4.5, 0]] },
  { key: "cut", name: "Тайрсан булан", sketch: [[0, 0], [0, 3], [3.2, 3], [4, 2.2], [4, 0]] },
  { key: "u", name: "П хэлбэр", sketch: [[0, 0], [0, 3.5], [1.5, 3.5], [1.5, 1.5], [3, 1.5], [3, 3.5], [4.5, 3.5], [4.5, 0]] },
];

export function emptyRoom(id: string, name: string, sketch: [number, number][]): RoomDraft {
  return {
    id,
    name,
    sketch,
    walls: sketch.map(() => null),
    readings: sketch.map(() => [null, null, null]),
    heights: sketch.map(() => []),
    diagonals: [],
    ceiling: null,
    openings: [],
    utilities: [],
    obstructions: [],
    zones: [],
    photo_ids: [],
  };
}

export function newProject(id: string, surveyor: string, now: string, owner = { org_id: "", user_id: "" }): Project {
  return {
    id,
    ...owner,
    title: "",
    deal_id: null,
    deal_title: null,
    created_at: now,
    updated_at: now,
    edited_at: now,
    site: { address: "", customer_name: "", customer_phone: "" },
    surveyor_name: surveyor,
    choices: {},
    picks: [],
    rooms: [],
    photos: [],
    notes: [],
    render: { base_photo_id: null, scene_id: null, scene_photo_id: null, items: [], results: [], approved: null },
    sign_off: null,
    revisions: [],
    seq: 0,
  };
}

export const OPENING_KINDS: Record<OpeningKind, string> = { door: "Хаалга", window: "Цонх", opening: "Нүх" };
export const UTILITY_KINDS: Record<UtilityKind, string> = {
  water_cold: "Хүйтэн ус",
  water_hot: "Халуун ус",
  drain: "Бохир",
  power: "Розетк",
  gas: "Хий",
  vent: "Салхивч",
  data: "Интернэт",
};
export const OBSTRUCTION_KINDS: Record<ObstructionKind, string> = {
  column: "Багана",
  boxing: "Хоолойн хайрцаг",
  beam: "Дам нуруу",
  radiator: "Радиатор",
  other: "Бусад",
};
export const ZONE_KINDS: Record<ZoneKind, string> = { base: "Доод", wall: "Дээд", tall: "Өндөр" };
export const PURPOSES: Record<PartitionPurpose, string> = {
  cabinets: "Шүүгээ",
  appliance: "Техник",
  sink: "Угаалтуур",
  hob: "Плита",
  open: "Задгай",
  filler: "Нөхөх хавтан",
};

/** Typical zone frames (mm): base run, wall cabinets, tall units. */
export const ZONE_DEFAULTS: Record<ZoneKind, { elevation: number; height: number; depth: number }> = {
  base: { elevation: 0, height: 870_000, depth: 600_000 },
  wall: { elevation: 1_450_000, height: 720_000, depth: 350_000 },
  tall: { elevation: 0, height: 2_100_000, depth: 600_000 },
};

export const STANDARD_WIDTHS_MM = [300, 400, 450, 500, 600, 800, 900];

const UNITS: Record<string, string> = { pcs: "ш", set: "иж", sheet: "хуудас", m: "м", m2: "м²", m3: "м³", kg: "кг", l: "л", pack: "боодол", roll: "ороомог" };

/** A catalog unit as the rep reads it (the API sends codes such as "pcs"). */
export function unitLabel(unit: string): string {
  return UNITS[unit] ?? unit;
}
